import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { fetchAdminMyModules } from "../services/authService";

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem("accessToken"));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem("refreshToken"));
  const [user, setUser] = useState(readStoredUser);

  const login = useCallback(({ access, refresh, user: userData }) => {
    localStorage.setItem("accessToken", access);
    localStorage.setItem("refreshToken", refresh);
    localStorage.setItem("user", JSON.stringify(userData));
    setAccessToken(access);
    setRefreshToken(refresh);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback((userData) => {
    if (userData) {
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
    }
  }, []);

  useEffect(() => {
    const role = String(user?.role || "").toUpperCase();
    const isAdminSide = ["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"].includes(role);
    if (!accessToken || !isAdminSide) return;
    let cancelled = false;
    fetchAdminMyModules()
      .then((res) => {
        if (cancelled) return;
        const effective = Array.isArray(res?.data?.effective_admin_permissions)
          ? res.data.effective_admin_permissions
          : [];
        setUser((prev) => {
          const nextUser = { ...(prev || {}), effective_admin_permissions: effective };
          localStorage.setItem("user", JSON.stringify(nextUser));
          return nextUser;
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accessToken, user?.role]);

  const value = useMemo(
    () => ({ accessToken, refreshToken, user, login, logout, refreshUser }),
    [accessToken, refreshToken, user, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
